import { buildProtectedAuthHeaders } from '../protectedNetwork'
import { AuthExpiredError } from '../authErrors'
import { SubmissionRejectedError } from '../submissionErrors'
import { dispatchAuthExpired } from '../authEvents'
import { apiRoutingConfig } from '../apiRouting'

export const API_BASE_URL = apiRoutingConfig.apiBaseUrl

export interface ApiError {
  error: string
  message?: string
  // Present on RFC-7807 problem+json responses from the API.
  code?: string
  detail?: string
  title?: string
}

export interface SubmitResponse {
  success: boolean
  id: string
  syncedAt: string
}

// 403 problem codes that are a terminal rejection of the submission itself
// (not an auth failure). Retrying won't help; the client marks them failed.
const TERMINAL_SUBMISSION_CODES = new Set(['submission_window_closed'])

export async function readApiError(response: Response): Promise<ApiError> {
  return response.json().catch(() => ({
    error: `HTTP ${response.status}: ${response.statusText}`,
  }))
}

/**
 * Treat only a 401 as "your token is missing/invalid" → dispatch session expiry
 * and throw AuthExpiredError so the app re-authenticates.
 *
 * A 403 must NOT sign the user out: it means the request was authenticated but
 * forbidden by a business rule (e.g. deletion not enabled, asset window
 * closed). Recorder endpoints authenticate via the API Gateway JWT authorizer +
 * `require_auth`, which return 401 (never 403) for token problems, so anything
 * 403 is genuinely "forbidden" and is left for the caller's normal error path.
 */
export function throwIfAuthRejected(response: Response, context: string): void {
  if (response.status === 401) {
    dispatchAuthExpired({ context, reason: 'http_401' })
    throw new AuthExpiredError()
  }
}

/**
 * For submission-write endpoints: a 403 carrying a known terminal code means the
 * server refused this submission (e.g. the asset's active period is closed).
 * Surfaces it as a typed, non-retryable error so the sync layer marks the
 * submission failed rather than retrying it. An unknown 403 is left for the
 * caller's normal error path (a generic Error — never a sign-out).
 */
export function throwIfSubmissionRejected(response: Response, error: ApiError): void {
  if (
    response.status === 403 &&
    error.code &&
    TERMINAL_SUBMISSION_CODES.has(error.code)
  ) {
    throw new SubmissionRejectedError(
      error.code,
      error.detail || error.title || 'This submission was rejected.',
      403,
    )
  }
}

function ensureWindowAvailable(): void {
  if (typeof window === 'undefined') {
    throw new Error('Network request attempted in a non-browser environment')
  }
}

export function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  timeoutMs: number,
): Promise<Response> {
  ensureWindowAvailable()

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

  if (init?.signal) {
    const upstream = init.signal
    if (upstream.aborted) {
      controller.abort()
    } else {
      upstream.addEventListener('abort', () => controller.abort(), { once: true })
    }
  }

  const nextInit: RequestInit = { ...(init ?? {}), signal: controller.signal }

  return fetch(input, nextInit).finally(() => {
    window.clearTimeout(timeout)
  })
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export { buildProtectedAuthHeaders }
